<template>
  <teleport to="body">
    <div role="dialog" aria-modal="true" :aria-label="t('nav.more')" class="fixed inset-0 z-40 lg:hidden">
      <div class="more-sheet-scrim fixed inset-0 bg-neutral-900/20" @click="emit('close')" />

      <div
        class="more-sheet-panel fixed inset-x-0 bottom-0 max-h-[75vh] overflow-y-auto rounded-t-2xl bg-white px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-xl"
      >
        <div class="mx-auto mt-1 mb-2 h-1 w-10 rounded-full bg-neutral-200" role="presentation" />

        <nav class="flex flex-col gap-1 pb-2">
          <template v-for="(groupItems, groupKey) in groupedItems" :key="groupKey">
            <h3 v-if="String(groupKey) !== 'ungrouped'" class="px-4 pt-3 pb-1 text-xs text-neutral-500">
              {{ te(String(groupKey)) ? t(String(groupKey)) : groupKey }}
            </h3>

            <router-link
              v-for="item in groupItems"
              :key="item.to"
              v-permission="{ ids: item.permissionIds, check: item.permissionCheck }"
              :to="item.to"
              active-class="active-menu-item"
              class="group flex items-center rounded-md py-3 px-4 transition-colors duration-150 ease-out-quart hover:bg-neutral-100/70"
              @click="emit('close')"
            >
              <component :is="getNavigationIcon(item.icon)" class="mr-3 h-5 w-5 text-neutral-700" />
              <span class="text-sm tracking-wide text-neutral-700">{{ te(item.name) ? t(item.name) : item.name }}</span>
            </router-link>
          </template>

          <div v-if="showSettings || showLogout" class="my-1 border-t border-neutral-100" role="presentation" />

          <button
            v-if="showSettings"
            type="button"
            class="group flex w-full items-center rounded-md py-3 px-4 text-left transition-colors duration-150 ease-out-quart hover:bg-neutral-100/70"
            @click="emit('settings')"
          >
            <component :is="SettingsIcon" class="mr-3 h-5 w-5 text-neutral-700" />
            <span class="text-sm tracking-wide text-neutral-700">{{ t('nav.settings') }}</span>
          </button>

          <button
            v-if="showLogout"
            type="button"
            class="group flex w-full items-center rounded-md py-3 px-4 text-left transition-colors duration-150 ease-out-quart hover:bg-neutral-100/70"
            @click="emit('logout')"
          >
            <font-awesome-icon :icon="['far', 'arrow-right-from-bracket']" class="mr-3 h-5 w-5 text-neutral-700" />
            <span class="text-sm tracking-wide text-neutral-700">{{ t('nav.log_out') }}</span>
          </button>
        </nav>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { vPermission } from '@infuse/iam/permission/directives';
import { MenuItem } from '@infuse/ui/domain';
import { onKeyStroke } from '@vueuse/core';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SettingsIcon from '../assets/infuse-icons/settings.svg?component';
import { getNavigationIcon } from './NavigationIcons';

interface Props {
  menuItems: MenuItem[];
  showSettings?: boolean;
  showLogout?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showSettings: false,
  showLogout: true
});

const emit = defineEmits(['close', 'settings', 'logout']);

const { t, te } = useI18n();

const groupedItems = computed(() =>
  props.menuItems.reduce((groups: Record<string, MenuItem[]>, item: MenuItem) => {
    const groupName = item.group || 'ungrouped';
    (groups[groupName] ??= []).push(item);
    return groups;
  }, {})
);

// Close on Escape — unless a modal is open on top; that one closes first.
onKeyStroke('Escape', () => {
  if (document.getElementById('modalRoot')) {
    return;
  }

  emit('close');
});
</script>

<style scoped>
/* Mount-only entrance: the sheet is toggled with v-if. Exits are instant by design. */
.more-sheet-panel {
  animation: more-sheet-in 300ms cubic-bezier(0.32, 0.72, 0, 1);
}

.more-sheet-scrim {
  animation: more-sheet-scrim-in 250ms cubic-bezier(0.23, 1, 0.32, 1);
}

@keyframes more-sheet-in {
  from {
    transform: translateY(100%);
  }
}

@keyframes more-sheet-scrim-in {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .more-sheet-panel {
    animation: more-sheet-scrim-in 200ms ease;
  }
}
</style>
